"use client";

import React, { useState } from "react";
import Image from "next/image";
import { MediaItem } from "@/type/commonTypes";

interface MediaFeaturedCardProps {
  item: MediaItem;
}

export function cleanExcerpt(content: string): string {
  if (!content) return "";
  return content
    .replace(/<[^>]*>?/gm, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .trim();
}

export default function MediaFeaturedCard({ item }: MediaFeaturedCardProps) {
  const [imgError, setImgError] = useState(false);
  const excerpt = cleanExcerpt(item.content);

  const fallbackImage = "/next_assets/images/careers/anniversary_party.png";

  return (
    <div className="bg-white rounded-3xl border border-[#EAECF0] shadow-xs overflow-hidden mb-12 sm:mb-16 grid grid-cols-1 lg:grid-cols-2 transition hover:shadow-md">
      {/* Left: Featured Image */}
      <div className="relative w-full h-250 sm:h-95 bg-[#0A1128] overflow-hidden">
        <Image
          src={imgError || !item.imageUrl ? fallbackImage : item.imageUrl}
          alt={item.title || "Featured Coverage"}
          fill
          priority
          sizes="(max-width: 1024px) 100vw, 50vw"
          className="object-cover object-center transition-transform duration-700 hover:scale-105"
          onError={() => setImgError(true)}
        />
        {item.linkType === "video" && (
          <div className="absolute inset-0 bg-black/25 flex items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-white/90 text-primaryBlue flex items-center justify-center shadow-lg transform transition group-hover:scale-110">
              <svg
                className="w-6 h-6 ml-0.5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        )}
      </div>

      {/* Right: Content */}
      <div className="p-4 sm:p-10 flex flex-col justify-between">
        {/* Header Row: Badge & Date */}
        <div className="flex items-center justify-between gap-3 mb-4 sm:mb-5">
          <span className="inline-block px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-[#FF5A5F] bg-[#FFF0F0] rounded-md">
            FEATURED
          </span>
          {item.postedOn && (
            <span className="text-xs sm:text-sm text-[#667085] font-medium ml-auto">
              {item.postedOn}
            </span>
          )}
        </div>

        {/* Title */}
        <h2
          title={item.title}
          className="text-xl sm:text-2xl sm:leading-8 font-extrabold text-[#15192C] mb-2.5 leading-snug line-clamp-2"
        >
          {item.href ? (
            <a
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primaryBlue transition-colors"
            >
              {item.title}
            </a>
          ) : (
            item.title
          )}
        </h2>

        {/* Excerpt */}
        {excerpt && (
          <p className="text-sm text-[#525C76] line-clamp-3 mb-5">{excerpt}</p>
        )}

        {/* Action Button */}
        {item.href && (
          <a
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center justify-start gap-3"
          >
            <p className="inline-flex items-center gap-2 bg-primaryBlue group-hover:bg-blue-700 text-white font-semibold text-sm px-6 py-2 sm:py-3 rounded-lg transition shadow-xs">
              <span>
                {item.linkText ||
                  (item.linkType === "video"
                    ? "Watch Video"
                    : "Read Full Coverage")}
              </span>
            </p>
            <svg
              className="w-4 h-4 text-primaryBlue group-hover:text-primaryBlue/60"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </a>
        )}
      </div>
    </div>
  );
}
